import { getDictionaryTags } from './http/dictionary';
import { escapeODataValue } from './http/helpers/generateOdataFilter';
import type { FetchQueryOptions, QueryObserverOptions } from '@tanstack/query-core';
import type { UseQueryOptions } from '@tanstack/react-query';
import type { ConcreteIndicator, IndicatorRef, Tag } from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import type { ConcreteIndicatorViewModel } from '@thinkalpha/ui-types/contracts/dictionary-view-model';
import type { ResourceQuery } from '@thinkalpha/ui-types/contracts/resource-query';
import {
    indicatorFromRefLoader,
    getFeaturedIndicators,
    getIndicatorsQuery,
    getIndicatorsViewModelQuery,
    getIndicatorViewModels,
} from 'src/lib/dictionary/dictionary';

export const DICTIONARY = 'dictionary';
const INDICATOR_FETCH_LIMIT = 5000;

export interface IndicatorContextFilter {
    universe?: boolean;
    backtestSupportedOnly?: boolean;
    indicatorKey?: string | readonly string[] | null;
}

export interface IndicatorTagTreeData {
    all: ConcreteIndicator[];
    owned: ConcreteIndicatorViewModel[];
}

export const getDictionaryTagsQuery = (): UseQueryOptions<Tag[]> => {
    return {
        queryKey: [DICTIONARY, 'tags'],
        queryFn: async () => getDictionaryTags(),
    };
};

export function getIndicatorCompletionOptionsViewModelQuery({
    query,
    resourceQuery,
}: {
    query?: string | null;
    resourceQuery?: ResourceQuery;
}): FetchQueryOptions<ConcreteIndicatorViewModel[]> {
    return {
        queryKey: [DICTIONARY, 'view models', query, resourceQuery],
        queryFn: async () => {
            const res = await getIndicatorsViewModelQuery({ search: query ?? undefined, ...resourceQuery });
            return res;
        },
    };
}

export function getIndicatorCompletionOptionsQuery({
    query,
    resourceQuery,
}: {
    query?: string | null;
    resourceQuery?: ResourceQuery;
}): FetchQueryOptions<ConcreteIndicator[]> {
    return {
        queryKey: [DICTIONARY, query, resourceQuery],
        queryFn: async () => {
            const res = await getIndicatorsQuery({ search: query ?? undefined, ...resourceQuery });
            return res;
        },
    };
}

export function getFeaturedIndicatorsQuery(): UseQueryOptions<ConcreteIndicator[]> {
    return {
        queryKey: [DICTIONARY, 'featured'],
        queryFn: () => getFeaturedIndicators(),
    };
}

export const getIndicatorFromRefQuery = (ref?: IndicatorRef): QueryObserverOptions<ConcreteIndicator> => {
    return {
        queryKey: [DICTIONARY, ref?.id],
        queryFn: () => indicatorFromRefLoader.load(ref!),
        enabled: Boolean(ref),
    };
};

export function getAlertingIndicatorsQuery(): QueryObserverOptions<ConcreteIndicatorViewModel[]> {
    return {
        queryKey: [DICTIONARY, 'alerting-indicators'],
        queryFn: async () => {
            const res = await getIndicatorsViewModelQuery({ filter: 'alerting eq true' });
            return res;
        },
    };
}

/**
 * Build the OData-style filter string used to constrain indicators to those
 * valid in the current code-editor context. Mirrors the predicates used by the
 * flat completion search so the tree shows the same population.
 */
function buildIndicatorContextFilter({
    universe,
    backtestSupportedOnly,
    indicatorKey,
}: IndicatorContextFilter): string {
    const parts = ['hidden eq false'];
    if (universe) parts.push('screener eq true');
    if (backtestSupportedOnly) parts.push('backtestSupported eq true');

    const namedKeys =
        indicatorKey == null
            ? []
            : Array.isArray(indicatorKey)
              ? indicatorKey.filter((k) => Boolean(k))
              : [indicatorKey];

    const keyClauses = ['key eq null', ...namedKeys.map((k) => `key eq '${escapeODataValue(k)}'`)];
    parts.push(keyClauses.length === 1 ? keyClauses[0] : `(${keyClauses.join(' or ')})`);

    return parts.join(' and ');
}

export function getDictionaryTagTreeIndicatorsQuery(
    context: IndicatorContextFilter,
): UseQueryOptions<IndicatorTagTreeData> {
    const filter = buildIndicatorContextFilter(context);
    return {
        queryKey: [DICTIONARY, 'tag-tree-indicators', filter],
        queryFn: async () => {
            const [all, owned] = await Promise.all([
                getIndicatorsQuery({ filter, limit: INDICATOR_FETCH_LIMIT }),
                getIndicatorViewModels({ permissions: 'owner' }),
            ]);
            return { all, owned };
        },
    };
}
